import React, { useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import {
  ITabGroupConfig,
  ITabPanel,
  ITabPanelProps,
} from "../../Typings/TabPanel";

function TabPanel(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`candidate-tabpanel-${index}`}
      aria-labelledby={`candidate-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

/**
 * Allows for the switching of tabs from outside the component
 *
 * @param config - The configuration for the tab group
 * @param tabs - The tabs object to search for the current tab
 * @param setValue - The function to set the value of the current tab
 */
function externalTabChange(
  config: ITabGroupConfig,
  tabs: ITabPanel[],
  setValue: (value: number) => void
) {
  const { currentTabState } = config;
  if (currentTabState) {
    const tabIndex = tabs.findIndex((tab) => tab.label === currentTabState);
    setValue(tabIndex);
  }
}

export default function TabGroup({ config }: { config: ITabGroupConfig }) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    config.changeTabState && config.changeTabState(config.tabs[newValue].label);
  };

  const { groupLabel, id, tabs } = config;

  const a11yProps = (index: number) => ({
    id: `${id}-tab-${index}`,
    "aria-controls": `${id}-tabpanel-${index}`,
  });

  useEffect(() => {
    externalTabChange(config, tabs, setValue);
  }, [config, tabs]);

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginBottom: "1rem",
        }}
      >
        <Tabs value={value} onChange={handleChange} aria-label={groupLabel}>
          {tabs.map((tab, idx) => (
            <Tab
              sx={{ flexGrow: 1, maxWidth: "100%" }}
              key={idx}
              label={tab.label}
              {...a11yProps(idx)}
            />
          ))}
        </Tabs>
      </Box>
      {tabs.map((tab, idx) => (
        <TabPanel value={value} index={idx} key={idx}>
          {tab.TabComponent}
        </TabPanel>
      ))}
    </Box>
  );
}
